/**
 * Google-Calendar-shaped recurrence builder.
 *
 * Emits a `RecurrenceSpec`, never a string: the caller turns it into an
 * RRULE body plus `until_ms` at submit time via `rrule.ts`, so the two
 * halves of an end date can never drift apart here.
 *
 * The plain-language summary is not decoration. An RRULE is the one part
 * of a schedule an operator cannot check by reading it back, and a rule
 * that repeats on the wrong weekday looks identical to a correct one
 * until a robot shows up on a Tuesday.
 */
import { DateField } from '../../design-system/components/DateField';
import {
  WEEKDAYS,
  describeRecurrence,
  isLastWeekdayOfMonth,
  nthWeekdayOfMonth,
  weekdayOf,
  type RecurrenceFreq,
  type RecurrenceSpec,
  type Weekday,
} from './rrule';
import './RecurrenceEditor.css';

const WEEKDAY_SHORT: Record<Weekday, string> = {
  SU: 'S',
  MO: 'M',
  TU: 'T',
  WE: 'W',
  TH: 'T',
  FR: 'F',
  SA: 'S',
};

const WEEKDAY_FULL: Record<Weekday, string> = {
  SU: 'Sunday',
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
};

const ORDINAL_WORD: Record<number, string> = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  [-1]: 'last',
};

export function RecurrenceEditor({
  spec,
  onChange,
  dtstart,
  timeZone,
}: {
  spec: RecurrenceSpec;
  onChange: (next: RecurrenceSpec) => void;
  /** The schedule's first start — the rule's day-of-week and
   *  day-of-month selectors are read off it, as in Google Calendar.
   *  Its LOCAL fields are the wall clock the operator typed; the
   *  instant they name is resolved in `timeZone`, not here. */
  dtstart: Date;
  /** The SITE's IANA zone — the clock this rule will actually run on.
   *  Named in the summary below because an unlabelled "09:00" is what
   *  let a browser-zone schedule pass review. */
  timeZone: string;
}) {
  function patch(next: Partial<RecurrenceSpec>) {
    onChange({ ...spec, ...next });
  }
  // The last day can be no earlier than the first: the start's own day.
  const pad = (n: number) => n.toString().padStart(2, '0');
  const untilMin = Number.isNaN(dtstart.getTime())
    ? undefined
    : `${dtstart.getFullYear()}-${pad(dtstart.getMonth() + 1)}-${pad(dtstart.getDate())}`;

  function toggleDay(day: Weekday) {
    const has = spec.byDay.includes(day);
    const next = has ? spec.byDay.filter((d) => d !== day) : [...spec.byDay, day];
    // An empty BYDAY is not a rule that never fires — `buildRRule` falls
    // back to the start day — but showing zero selected chips says the
    // opposite, so the last one cannot be cleared.
    if (next.length === 0) return;
    patch({ byDay: next });
  }

  const nth = nthWeekdayOfMonth(dtstart);
  const startWeekday = weekdayOf(dtstart);
  // "The last Friday" beside "the fourth Friday", from a start that is
  // both (the 22nd-28th); from the 29th-31st the nth already says last.
  const lastOffered = nth !== -1 && isLastWeekdayOfMonth(dtstart);
  // A "last" the start no longer is writes the nth (`buildRRule`).
  const monthlyMode =
    spec.monthlyMode === 'last_weekday' && !lastOffered ? 'nth_weekday' : spec.monthlyMode;

  return (
    <div className="nx-recurrence" data-testid="recurrence-editor">
      <div className="nx-recurrence__row">
        <span className="nx-recurrence__label">Repeat every</span>
        <input
          type="number"
          min={1}
          max={99}
          className="nx-recurrence__interval"
          value={spec.interval}
          onChange={(e) => patch({ interval: Math.max(1, Math.min(99, Number(e.target.value))) })}
          aria-label="Repeat interval"
          data-testid="recurrence-interval"
        />
        <select
          value={spec.freq}
          onChange={(e) => patch({ freq: e.target.value as RecurrenceFreq })}
          aria-label="Repeat frequency"
          data-testid="recurrence-freq"
        >
          <option value="DAILY">{spec.interval > 1 ? 'days' : 'day'}</option>
          <option value="WEEKLY">{spec.interval > 1 ? 'weeks' : 'week'}</option>
          <option value="MONTHLY">{spec.interval > 1 ? 'months' : 'month'}</option>
        </select>
      </div>

      {spec.freq === 'WEEKLY' && (
        <div className="nx-recurrence__row nx-recurrence__row--days">
          <span className="nx-recurrence__label">On</span>
          <div className="nx-recurrence__days" role="group" aria-label="Repeat on weekdays">
            {WEEKDAYS.map((day) => {
              const on = spec.byDay.includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  className={`nx-recurrence__day${on ? ' nx-recurrence__day--on' : ''}`}
                  aria-pressed={on}
                  aria-label={WEEKDAY_FULL[day]}
                  onClick={() => toggleDay(day)}
                  data-testid={`recurrence-day-${day}`}
                >
                  {WEEKDAY_SHORT[day]}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {spec.freq === 'MONTHLY' && (
        <div className="nx-recurrence__row">
          <span className="nx-recurrence__label">Pattern</span>
          <select
            value={monthlyMode}
            onChange={(e) =>
              patch({ monthlyMode: e.target.value as RecurrenceSpec['monthlyMode'] })
            }
            aria-label="Monthly pattern"
            data-testid="recurrence-monthly-mode"
          >
            <option value="day_of_month">On day {dtstart.getDate()}</option>
            <option value="nth_weekday">
              On the {ORDINAL_WORD[nth] ?? `${nth}th`} {WEEKDAY_FULL[startWeekday]}
            </option>
            {lastOffered && (
              <option value="last_weekday">On the last {WEEKDAY_FULL[startWeekday]}</option>
            )}
          </select>
        </div>
      )}

      <div className="nx-recurrence__row">
        <span className="nx-recurrence__label">Ends</span>
        <select
          value={spec.end}
          onChange={(e) => patch({ end: e.target.value as RecurrenceSpec['end'] })}
          aria-label="Recurrence end"
          data-testid="recurrence-end"
        >
          <option value="never">Never</option>
          <option value="on">On date</option>
          <option value="after">After N times</option>
        </select>
        {spec.end === 'on' && (
          // Masked to the house DD/MMM/YYYY: a bare `<input type="date">`
          // draws its own locale's format, so this field and the start
          // time above it disagreed about what a date looks like on any
          // en-US browser.
          <DateField
            value={spec.untilDate}
            min={untilMin}
            onChange={(v) => patch({ untilDate: v })}
            ariaLabel="Repeat until"
            data-testid="recurrence-until"
          />
        )}
        {spec.end === 'after' && (
          <input
            type="number"
            min={1}
            max={999}
            className="nx-recurrence__interval"
            value={spec.count}
            onChange={(e) => patch({ count: Math.max(1, Math.min(999, Number(e.target.value))) })}
            aria-label="Number of runs"
            data-testid="recurrence-count"
          />
        )}
      </div>

      {/* There was a "Calendar colour" swatch row here. It is gone: a
          schedule's colour is its CATEGORY's colour, always, so the
          grid's colours mean one thing rather than whatever each author
          happened to pick — and the picker was only ever reachable for
          RECURRING schedules, so one-off calendar entries could not be
          coloured at all and the two were already inconsistent. The
          `color` column and the API field are untouched (nullable,
          defaulted, PATCH-only-if-present); the SPA simply stops writing
          and reading them. */}

      <p className="nx-recurrence__summary" data-testid="recurrence-summary">
        {describeRecurrence(spec, dtstart, timeZone)}
      </p>
    </div>
  );
}
